import { MarketingTeam } from "@redotech/redo-marketing-core-common/marketing/marketing-team-zod";
import { Provider } from "@redotech/redo-order-common/provider";
import { ProductSelectionType } from "./email-builder";
import {
  SmsAttachmentType,
  SmsContactCardAttachment,
  SmsImageAttachment,
  SmsTemplate,
} from "./sms-template";

/**
 * Helper function to get the image attachment from an sms template
 */
export const getImageAttachment = (
  template: SmsTemplate | null | undefined,
): SmsImageAttachment | undefined => {
  if (!template) {
    return undefined;
  }
  const imageAttachment = template.attachments?.find(
    (att) =>
      att.type === SmsAttachmentType.IMAGE ||
      att.type === SmsAttachmentType.PRODUCT_IMAGE,
  );

  // Type guard to ensure we return the correct type
  if (imageAttachment?.type === SmsAttachmentType.CONTACT_CARD) {
    throw new Error("Unexpected attachment type, expected image attachment");
  }

  return imageAttachment;
};

export const getImageAttachmentProvider = (
  attachment: SmsImageAttachment | undefined,
): Provider | undefined | null =>
  attachment && "provider" in attachment ? attachment.provider : undefined;

/**
 * Helper function to update the image attachment in an sms template
 */
export const updateImageAttachment = (
  attachments: SmsTemplate["attachments"] = [],
  newAttachment: SmsImageAttachment | null | undefined,
) => {
  const nonImageAttachments = attachments.filter(
    (att) =>
      att.type !== SmsAttachmentType.IMAGE &&
      att.type !== SmsAttachmentType.PRODUCT_IMAGE,
  );

  return newAttachment
    ? [...nonImageAttachments, newAttachment]
    : nonImageAttachments;
};

/**
 * Helper function to get the contact card attachment from an sms template
 */
export const getContactCardAttachment = (
  template: SmsTemplate | null | undefined,
): SmsContactCardAttachment | undefined => {
  if (!template) {
    return undefined;
  }
  const contactCardAttachment = template.attachments?.find(
    (att) => att.type === SmsAttachmentType.CONTACT_CARD,
  );
  return contactCardAttachment;
};

/**
 * Helper function to add a contact card attachment to an sms template if it doesn't already exist
 */
export const addContactCardAttachment = ({
  template,
}: {
  template: SmsTemplate;
}) => {
  if (getContactCardAttachment(template)) {
    return template.attachments;
  }

  const contactCardAttachment: SmsContactCardAttachment = {
    type: SmsAttachmentType.CONTACT_CARD,
  };

  return [...(template.attachments ?? []), contactCardAttachment];
};

/**
 * Helper function to remove the contact card attachment from an sms template if it exists
 */
export const removeContactCardAttachment = ({
  template,
}: {
  template: SmsTemplate;
}) => {
  if (!getContactCardAttachment(template)) {
    return template.attachments || [];
  }

  const nonContactCardAttachments = template.attachments?.filter(
    (att) => att.type !== SmsAttachmentType.CONTACT_CARD,
  );

  return nonContactCardAttachments || [];
};

/**
 * Helper function to create a properly typed image attachment
 */
export const createImageAttachment = (
  type: SmsAttachmentType.IMAGE | SmsAttachmentType.PRODUCT_IMAGE,
  existing?: SmsImageAttachment,
  provider?: Provider | null,
): SmsImageAttachment => {
  return {
    type,
    productSelectionType:
      existing?.productSelectionType ?? ProductSelectionType.DYNAMIC,
    imageSrc: existing?.imageSrc,
    manuallySelectedProduct: existing?.manuallySelectedProduct,
    productFilterId: existing?.productFilterId,
    provider: provider ?? existing?.provider,
  };
};

/**
 * Helper function to set the image attachment type, preserving existing data
 */
export const setImageAttachmentType = (
  attachments: SmsTemplate["attachments"] = [],
  type: SmsAttachmentType.IMAGE | SmsAttachmentType.PRODUCT_IMAGE,
  provider?: Provider | null,
) => {
  const currentImageAttachment = attachments?.find(
    (att) =>
      att.type === SmsAttachmentType.IMAGE ||
      att.type === SmsAttachmentType.PRODUCT_IMAGE,
  );

  const existingImageAttachment =
    currentImageAttachment?.type === SmsAttachmentType.CONTACT_CARD
      ? undefined
      : currentImageAttachment;

  const newAttachment = createImageAttachment(
    type,
    existingImageAttachment,
    provider,
  );
  return updateImageAttachment(attachments, newAttachment);
};

/**
 * Helper function to determine if a template will result in an MMS message.
 * Used for cost estimation and UI display.
 */
export function willTemplateResultInMms(
  template: SmsTemplate,
  team: MarketingTeam,
): boolean {
  // Check image attachments
  const imageAttachment = getImageAttachment(template);
  const hasValidImage =
    !!imageAttachment &&
    (imageAttachment.type === SmsAttachmentType.PRODUCT_IMAGE ||
      !!imageAttachment.imageSrc);

  // Check contact card
  const contactCardAttachment = getContactCardAttachment(template);
  const hasValidContactCard =
    !!contactCardAttachment &&
    !!team.settings.brandKit.contactCard?.contactCardSrc;

  return hasValidImage || hasValidContactCard;
}
